// 権限マップ API クライアント。既存の user-reconcile-api / snapshot-api と同じ流儀
// （ApiClient に認証・エラー正規化を委譲）。backend PermissionMapResult に対応する DTO。
// 型は @cloud-checker/shared の正本（PermissionKey / Severity）をそのまま使い、独自に再定義しない。
// permission を string に緩めると、この値で突き合わせる表示側（filterUsers の
// `g.permission === filter.permission` / buildHeatmapRows の `g.permission === key`）が
// 綴り違い・旧キーをコンパイルで弾けず、一致 0 件＝「その危険権限は持っていない」という
// 嘘の表示になる。
import type { PermissionKey, Severity } from "@cloud-checker/shared";
import type { ApiClient } from "../api/client";

export interface PermissionMapApiDeps {
  readonly apiClient: ApiClient;
}

export interface PermissionGrantDto {
  readonly permission: PermissionKey;
  readonly label: string;
  readonly severity: Severity;
  readonly source: "profile" | "permission-set";
  readonly sourceLabel: string;
}
export interface PermissionMapUserDto {
  readonly id: string;
  readonly name: string;
  readonly username: string;
  readonly profile: string;
  readonly permissionSetLabels: string[];
  readonly grants: PermissionGrantDto[];
  /**
   * プロファイル / 割当先の権限セットを特定できず、実効権限を確定できなかったユーザー。
   * **表示契約**: true のユーザーを「危険権限なし」として描画してはならない。`grants` が
   * 空であっても未確定であることを必ず表に出す（spec: 偽の合格を出さない）。
   */
  readonly hasUnresolvedAssignment: boolean;
}
export interface PermissionSetSummaryDto {
  readonly id: string;
  readonly label: string;
  readonly isOwnedByProfile: boolean;
  /**
   * 割当行（PermissionSetAssignment）から数えた保有者数。無効化済みユーザーの割当や、
   * SF から返る期限切れ（ExpirationDate 経過）の割当も含むため**意図的に過大側**に出る
   * （backend salesforce/audit/permission-map.ts）。「0 件＝未使用」の判定には使えるが、
   * 正確な人数として見せてはならない。
   */
  readonly holderCount: number;
  readonly dangerCount: number;
}
export interface PermissionMapDto {
  readonly runAt: string;
  readonly users: PermissionMapUserDto[];
  readonly permissionSets: PermissionSetSummaryDto[];
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null;
}

/**
 * JSON 境界の最小限の形チェック（fail-closed）。API と SPA は別々にデプロイされるため版ずれが
 * 起こり得る。特に `hasUnresolvedAssignment` が欠けた応答を素通しすると `undefined`＝falsy として
 * 「確定済み・危険権限なし」に化けるため、不明な場合は false に正規化せず throw して画面全体を
 * エラーにする（spec: 部分データで描画しない）。
 */
function assertPermissionMapDto(body: unknown): asserts body is PermissionMapDto {
  if (!isRecord(body) || !Array.isArray(body.users) || !Array.isArray(body.permissionSets)) {
    throw new Error("権限マップの応答形式が不正です（users / permissionSets）");
  }
  for (const user of body.users) {
    if (!isRecord(user) || typeof user.hasUnresolvedAssignment !== "boolean" || !Array.isArray(user.grants)) {
      throw new Error("権限マップの応答形式が不正です（hasUnresolvedAssignment / grants）");
    }
  }
}

// GET /sf/permissions/map → PermissionMapDto（ユーザー単位の実効権限 + 権限セット集計）
export async function fetchPermissionMap(deps: PermissionMapApiDeps): Promise<PermissionMapDto> {
  const body = await deps.apiClient.get<unknown>("/sf/permissions/map");
  assertPermissionMapDto(body);
  return body;
}
